import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

import { buildMrtColumns } from '../../components/product-selections/columns';
import VisibleFirstColumnsButton from '../../components/VisibleFirstColumnsButton';
import { buildOwnedMrtLayout } from '../../components/detailPanel/utils/detailPanelTableLayout';

const useProductSelectionsMrtTable = ({
  legacyColumns,
  sendToStagingColumn,
  enrichedRows,
  externalOnRowClick,
  onOpenDetail,
  onNavigateToDealer,
  rowSelection,
  handleSelectionModelChange,
  columnVisibility,
  setColumnVisibility,
  columnOrder,
  setColumnOrder,
  columnPinning,
  setColumnPinning,
  sorting,
  setSorting,
  globalFilter,
  handleGlobalFilterChange,
  columnFilters,
  setColumnFilters,
  tableInitialState,
  maximizeActive,
  handleIsFullScreenChange,
  handleResetFilters,
  enableInternalScroll,
  isMobile,
  isVitest,
  minTableHeight,
  tableContainerTestId,
  hideMaximize,
}) => {
  const [filterSelectOptionsById, setFilterSelectOptionsById] = useState({});
  const tableTouchStateRef = useRef(null);

  const mrtColumns = useMemo(
    () => buildMrtColumns({ legacyColumns, sendToStagingColumn, filterSelectOptionsById }),
    [filterSelectOptionsById, legacyColumns, sendToStagingColumn]
  );

  const layoutProps = useMemo(
    () =>
      buildOwnedMrtLayout({
        enableInternalScroll,
        minHeight: minTableHeight,
        internalTableContainerSx: {
          flex: 1,
          height: maximizeActive ? '100%' : isMobile ? '60vh' : '100%',
          minHeight: maximizeActive ? 0 : isMobile ? '50vh' : 0,
          maxHeight: maximizeActive ? '100%' : isMobile ? '60vh' : '100%',
          overflowY: 'auto',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-x pan-y',
          '@supports (height: 100dvh)': {
            height: maximizeActive ? '100%' : isMobile ? '60dvh' : '100%',
            minHeight: maximizeActive ? 0 : isMobile ? '50dvh' : 0,
            maxHeight: maximizeActive ? '100%' : isMobile ? '60dvh' : '100%',
          },
        },
        internalTablePaperSx: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        },
      }),
    [enableInternalScroll, isMobile, maximizeActive, minTableHeight]
  );
  const tableContainerProps = useMemo(
    () => ({
      ...(layoutProps.muiTableContainerProps || {}),
      ...(tableContainerTestId ? { 'data-testid': tableContainerTestId } : {}),
      onWheel: (event) => {
        const horizontalDelta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
        if (!horizontalDelta) return;
        event.currentTarget.scrollLeft += horizontalDelta;
      },
      onKeyDown: (event) => {
        if (event.key === 'ArrowRight') {
          event.currentTarget.scrollLeft += 48;
        } else if (event.key === 'ArrowLeft') {
          event.currentTarget.scrollLeft -= 48;
        }
      },
      onTouchStart: (event) => {
        const touch = event.touches?.[0];
        if (!touch) return;
        tableTouchStateRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          scrollLeft: event.currentTarget.scrollLeft,
          scrollTop: event.currentTarget.scrollTop,
        };
      },
      onTouchMove: (event) => {
        const touch = event.touches?.[0];
        const state = tableTouchStateRef.current;
        if (!touch || !state) return;
        event.currentTarget.scrollLeft = state.scrollLeft + state.x - touch.clientX;
        event.currentTarget.scrollTop = state.scrollTop + state.y - touch.clientY;
      },
      onTouchEnd: () => {
        tableTouchStateRef.current = null;
      },
      onTouchCancel: () => {
        tableTouchStateRef.current = null;
      },
    }),
    [layoutProps.muiTableContainerProps, tableContainerTestId]
  );

  const table = useMaterialReactTable({
    columns: mrtColumns,
    data: enrichedRows,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.id ?? ''),
    enableRowSelection: true,
    // JSDOM (Vitest) doesn't provide real layout measurements, which breaks row virtualization
    // and makes tests unable to find row content. Keep virtualization on in real UI.
    enableRowVirtualization: enableInternalScroll ? !isVitest : false,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    enableColumnDragging: false,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableSorting: true,
    enableFacetedValues: true,
    enableFullScreenToggle: !hideMaximize,
    columnFilterDisplayMode: 'popover',
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const original = row.original;
        if (typeof externalOnRowClick === 'function') {
          externalOnRowClick({ row: original });
          return;
        }
        if (typeof onOpenDetail === 'function') onOpenDetail(original);
      },
      onDoubleClick: () => {
        const bac = row?.original?.bac;
        if (bac && typeof onNavigateToDealer === 'function') onNavigateToDealer(bac);
      },
      sx: { cursor: onOpenDetail ? 'pointer' : 'default' },
    }),
    state: {
      rowSelection,
      columnVisibility,
      columnOrder,
      columnPinning,
      sorting,
      globalFilter,
      columnFilters,
      isFullScreen: maximizeActive,
    },
    onIsFullScreenChange: handleIsFullScreenChange,
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater(rowSelection)
          : updater;
      const ids = Object.entries(next || {})
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key);
      handleSelectionModelChange(ids);
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    onGlobalFilterChange: handleGlobalFilterChange,
    onColumnFiltersChange: setColumnFilters,
    initialState: tableInitialState,
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        'data-testid': `selections-header-${column.id}`,
        sx: {
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 1,
            pointerEvents: 'auto',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    muiFilterTextFieldProps: ({ column }) => ({
      inputProps: {
        'data-testid': `selections-filter-input-${column.id}`,
      },
    }),
    renderToolbarInternalActions: ({ table }) => {
      const {
        columnFilterDisplayMode,
        enableColumnFilters,
        enableColumnOrdering,
        enableColumnPinning,
        enableDensityToggle,
        enableFilters,
        enableFullScreenToggle,
        enableGlobalFilter,
        enableHiding,
        initialState,
      } = table.options;
      const { columnFilters: activeColumnFilters, globalFilter: activeGlobalFilter } = table.getState();
      const hasActiveFilters = Boolean(activeGlobalFilter) || (activeColumnFilters?.length || 0) > 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {enableFilters && enableGlobalFilter && !(initialState?.showGlobalFilter) ? (
            <MRT_ToggleGlobalFilterButton table={table} />
          ) : null}
          <Tooltip title="Reset filters">
            <span>
              <IconButton size="small" onClick={() => handleResetFilters(table)} disabled={!hasActiveFilters}>
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {enableFilters && enableColumnFilters && columnFilterDisplayMode !== 'popover' ? (
            <MRT_ToggleFiltersButton table={table} />
          ) : null}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) ? (
            <VisibleFirstColumnsButton table={table} />
          ) : null}
          {enableDensityToggle ? <MRT_ToggleDensePaddingButton table={table} /> : null}
          {enableFullScreenToggle ? <MRT_ToggleFullScreenButton table={table} /> : null}
        </Box>
      );
    },
    renderTopToolbarCustomActions: () => null,
    muiTableContainerProps: tableContainerProps,
    muiTablePaperProps: layoutProps.muiTablePaperProps,
    muiTopToolbarProps: layoutProps.muiTopToolbarProps,
    muiBottomToolbarProps: layoutProps.muiBottomToolbarProps,
  });

  useEffect(() => {
    if (!table || typeof table.getAllLeafColumns !== 'function') return;
    const next = {};
    table.getAllLeafColumns().forEach((column) => {
      if (!column?.columnDef || column.columnDef.filterVariant !== 'multi-select') return;
      const faceted = column.getFacetedUniqueValues?.();
      if (!faceted) return;
      const options = Array.from(faceted.keys())
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      next[column.id] = options;
    });

    setFilterSelectOptionsById((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length) return next;
      for (const key of nextKeys) {
        const prevList = prev[key] || [];
        const nextList = next[key] || [];
        if (prevList.length !== nextList.length) return next;
        for (let index = 0; index < nextList.length; index += 1) {
          if (prevList[index] !== nextList[index]) return next;
        }
      }
      return prev;
    });
  }, [columnFilters, enrichedRows, globalFilter, table]);

  return {
    table,
    containerSx: layoutProps.containerSx,
    tableWrapperSx: layoutProps.tableWrapperSx,
  };
};

export default useProductSelectionsMrtTable;
